// parseFriendlyAddress adapted from https://github.com/toncenter/tonweb
// license: MIT

import type { StringAddress } from "./address";
import type { TONType } from "./network";

const bounceable_tag = 0x11;
const non_bounceable_tag = 0x51;
const test_flag = 0x80;

export type TONAddr = {
  isTestOnly: boolean;
  workchain: number;
  hashPart: Uint8Array;
  isBounceable: boolean;
};

/**
 * @param str {string}
 * @param size  {number}
 * @return {Uint8Array}
 */
function stringToBytes(str: string, size = 1): Uint8Array {
  let buf: ArrayBuffer;
  let bufView: Uint8Array | Uint16Array | Uint32Array;
  if (size === 1) {
    buf = new ArrayBuffer(str.length);
    bufView = new Uint8Array(buf);
  }
  if (size === 2) {
    buf = new ArrayBuffer(str.length * 2);
    bufView = new Uint16Array(buf);
  }
  if (size === 4) {
    buf = new ArrayBuffer(str.length * 4);
    bufView = new Uint32Array(buf);
  }
  for (let i = 0, strLen = str.length; i < strLen; i++) {
    bufView[i] = str.charCodeAt(i);
  }
  return new Uint8Array(bufView.buffer);
}

// todo: base64 decoding process could ignore one extra character at the end of string and the byte-length check below won't be able to catch it.
function base64toString(base64) {
  if (typeof self === "undefined") {
    return Buffer.from(base64, "base64").toString("binary"); // todo: (tolya-yanot) Buffer silently ignore incorrect base64 symbols, we need to throw error
  }
  return atob(base64);
}

/**
 * @param data  {ArrayLike<number>}
 * @return {Uint8Array}
 */
function crc16(data: ArrayLike<number>): Uint8Array {
  const poly = 0x1021;
  let reg = 0;
  const message = new Uint8Array(data.length + 2);
  message.set(data);
  for (const byte of message) {
    let mask = 0x80;
    while (mask > 0) {
      reg <<= 1;
      if (byte & mask) {
        reg += 1;
      }
      mask >>= 1;
      if (reg > 0xffff) {
        reg &= 0xffff;
        reg ^= poly;
      }
    }
  }
  return new Uint8Array([Math.floor(reg / 256), reg % 256]);
}

/**
 * @param addressString {string}
 * @return {TONAddr}
 */
export const parseFriendlyAddress = (addressString: string): TONAddr => {
  if (addressString.length !== 48) {
    throw new Error(
      "User-friendly address should contain strictly 48 characters"
    );
  }
  const data = stringToBytes(base64toString(addressString));
  if (data.length !== 36) {
    // 1byte tag + 1byte workchain + 32 bytes hash + 2 byte crc
    throw "Unknown address type: byte length is not equal to 36";
  }
  const addr = data.slice(0, 34);
  const crc = data.slice(34, 36);
  const calcedCrc = crc16(addr);
  if (!(calcedCrc[0] === crc[0] && calcedCrc[1] === crc[1])) {
    throw "Wrong crc16 hashsum";
  }
  let tag = addr[0];
  let isTestOnly = false;
  let isBounceable = false;
  if (tag & test_flag) {
    isTestOnly = true;
    tag = tag ^ test_flag;
  }
  if (tag !== bounceable_tag && tag !== non_bounceable_tag)
    throw "Unknown address tag";

  isBounceable = tag === bounceable_tag;

  let workchain = null;
  if (addr[1] === 0xff) {
    // TODO we should read signed integer here
    workchain = -1;
  } else {
    workchain = addr[1];
  }
  if (workchain !== 0 && workchain !== -1)
    throw new Error(`Invalid address wc ${workchain}`);

  const hashPart = addr.slice(2, 34);
  return { isTestOnly, isBounceable, workchain, hashPart };
};

// export const isEqualTON = (addr1: TONAddr, addr2: TONAddr) =>
//   addr1.isTestOnly === addr2.isTestOnly &&
//   addr1.isBounceable === addr2.isBounceable &&
//   addr1.workchain === addr2.workchain &&
//   addr1.hashPart.length === addr2.hashPart.length &&
//   addr1.hashPart.reduce(
//     (acc, cur, i) => acc && cur === addr2.hashPart.at(i),
//     true
//   );

export const isEqualTON = (addr1: TONAddr, addr2: TONAddr) => {
  if (
    addr1.isTestOnly !== addr2.isTestOnly ||
    addr1.isBounceable !== addr2.isBounceable ||
    addr1.workchain !== addr2.workchain ||
    addr1.hashPart.length !== addr2.hashPart.length
  ) {
    return false;
  }

  for (let i = 0; i < addr1.hashPart.length; i++) {
    if (addr1.hashPart[i] !== addr2.hashPart[i]) {
      return false;
    }
  }

  return true;
};

/**
 * @param tonAddr {TONAddr}
 * @return {string} Address as a user-friendly string
 */
export const stringifyFriendlyAddress = (
  tonAddr: TONAddr
): StringAddress<TONType> => {
  // Determine the tag based on isBounceable and isTestOnly flags
  let tag = tonAddr.isBounceable ? bounceable_tag : non_bounceable_tag;
  if (tonAddr.isTestOnly) {
    tag |= test_flag;
  }

  // Construct the address bytes array
  const addr = new Uint8Array(34);
  addr[0] = tag;
  addr[1] = tonAddr.workchain === -1 ? 0xff : tonAddr.workchain;

  // Copy the hash part into the address
  addr.set(tonAddr.hashPart, 2);

  // Calculate CRC16 on addr (without CRC bytes at the end)
  const crc = crc16(addr.slice(0, 34));

  // Concatenate addr and crc to form the final data array
  const data = new Uint8Array(36);
  data.set(addr, 0);
  data.set(crc, 34);

  // Convert data to Base64 and return as user-friendly address
  return btoa(String.fromCharCode(...data)) as StringAddress<TONType>;
};
